// The top-level team/user/repo scope bar (AL-100), a sibling of the window
// selector. Fetches /api/filters (what THIS viewer may pick — members get only
// their own team, admins all), holds the choice in state.scope, and on change
// re-runs whatever the mounting view registered so every metric re-scopes. The
// server clamps anything illegal, so the client just forwards the selection.
//
// The same controls mount on more than one tab (Metrics and Skills), so the
// renderer is parameterized by a CONTAINER element + an onChange callback, and
// all lookups are scoped to that container with CLASSES (never document-wide
// ids) — two mounts can coexist in the DOM without colliding.
import { state, $, esc, get, scopeQs } from './core'
import { msHtml, msWire } from './multiselect'
import { loadKpis, renderOpenMetric } from './kpis'
import { loadFacets, renderDists } from './facets'
import { renderSrControls } from './metrics/successRate'

// The scope bar currently on screen: which container it lives in and what to
// re-run when the scope changes. refreshFilterOptions() repaints THIS mount, so
// a team switch rebuilds the controls wherever the user is (Metrics or Skills).
interface ScopeMount {
  el: HTMLElement
  onChange: () => void
  // When true, the user picker is omitted — the Recommendations tab is team/org
  // grain only (never per-user, design §0.1), so a user axis would be redundant.
  noUser?: boolean
}
var mount: ScopeMount | null = null

// Fetch the viewer's filter options once, seed the default scope, then paint the
// bar into the dashboard's container. Called at boot (after the app shell is up).
// Returns the fetch promise so boot can sequence the first dashboard load after
// the default team is known.
export function loadFilterBar() {
  return get('/api/filters').then(function (opts) {
    state.filterOpts = opts || { role: 'member', teams: [], allTeams: [], repos: [], defaultTeamId: null, canPickUser: false, users: [] };
    // A team-bound viewer's default scope is their team (defaultTeamId); admins
    // start global (null). We only seed if the user hasn't already picked
    // (state.scope empty), so a re-fetch never stomps an active selection.
    if (!state.scope.team && state.filterOpts.defaultTeamId) {
      state.scope.team = state.filterOpts.defaultTeamId;
    }
    var bar = $('#filter-bar');
    if (bar) mountScopeBar(bar, onScopeChange);
  });
}

// Mount the scope controls into `container`, re-running `onChange` on any change.
// Records the mount so refreshFilterOptions() knows where to repaint.
export function mountScopeBar(container: HTMLElement, onChange: () => void, opts?: { noUser?: boolean }) {
  mount = { el: container, onChange: onChange, noUser: opts?.noUser };
  renderScopeControls();
}

// True when the viewer has any scope control to offer — lets a view decide
// whether to render the "Scope" bar chrome at all. Pass { noUser: true } to
// discount the user axis (the Recommendations tab doesn't offer it).
export function hasScopeControls(opts?: { noUser?: boolean }): boolean {
  var o = state.filterOpts;
  if (!o) return false;
  // User scope is hidden from the bar (Team + Repo only), so it no longer
  // decides whether the bar renders.
  return (o.role === 'admin' && o.teams.length > 0) || o.repos.length > 0;
}

// Render the per-role scope controls into the active mount:
//   member    — repo chips only (their scope is pinned to self server-side);
//   team-lead — user select ("Team total" + Me + own-team users) + repo chips;
//   admin     — team select + user select + repo chips.
// The server clamps every axis regardless — these controls only offer what
// /api/filters said is real.
function renderScopeControls() {
  if (!mount) return;
  var bar = mount.el;
  var o = state.filterOpts;
  if (!o) { bar.innerHTML = ''; return; }
  var isAdmin = o.role === 'admin';
  var parts = [];
  if (isAdmin && o.teams.length) {
    var teamOpts = '<option value="">All teams</option>';
    o.teams.forEach(function (t) {
      teamOpts += '<option value="' + esc(t.id) + '"' + (state.scope.team === t.id ? ' selected' : '') + '>' + esc(t.name) + '</option>';
    });
    parts.push('<span class="fb-grp"><label class="fb-lbl">Team</label><select class="fb-sel fb-team">' + teamOpts + '</select></span>');
  }
  // The User selector is deliberately NOT rendered (scope = Team + Repo only,
  // per design). The server still accepts a user scope — the control is hidden,
  // not the capability — so userScopeOpts stays for an easy re-enable.
  // Repo selector: MULTI-select (OR-set), painted into its own span so a repo
  // add/remove can repaint JUST the repo controls (keeping the team select intact
  // and not thrashing its handlers).
  if (o.repos.length) {
    parts.push('<span class="fb-grp"><label class="fb-lbl">Repo</label><span class="fb-repos"></span></span>');
  }
  bar.innerHTML = parts.join('');

  var teamSel = bar.querySelector('.fb-team') as HTMLSelectElement | null;
  if (teamSel) teamSel.onchange = function () { onTeamChange(teamSel!.value); };
  if (o.repos.length) paintRepoScope();
}

// Kept for existing callers (home.ts scope control repaints the dashboard bar).
export function renderFilterBar() {
  renderScopeControls();
}

// Options for the scope user picker, from filterOpts.users (the role-gated list:
// own team for a lead, everyone — or the picked team — for admin). The default ''
// reads as the role's natural aggregate: a lead's team total, an admin's all-users.
function userScopeOpts(): string {
  var o = state.filterOpts!;
  var cur = state.scope.user;
  var defLabel = o.role === 'admin' ? 'All users' : 'Team total';
  var opts = '<option value="">' + defLabel + '</option>' +
    '<option value="me"' + (cur === 'me' ? ' selected' : '') + '>Me</option>';
  var myId = (state.me && state.me.id) || '';
  (o.users || []).forEach(function (u) {
    if (u.id === myId) return; // 'Me' already covers the viewer
    opts += '<option value="' + esc(u.id) + '"' + (cur === u.id ? ' selected' : '') + '>' + esc(u.name || u.email) + '</option>';
  });
  return opts;
}

// The repo scope as the shared multiselect ("Repos · N") — empty = all repos.
function paintRepoScope(): void {
  if (!mount) return;
  var box = mount.el.querySelector('.fb-repos') as HTMLElement | null;
  var o = state.filterOpts;
  if (!box || !o) return;
  var sel = state.scope.repos;
  box.innerHTML = msHtml('fb-repo', 'Repos', sel.length);
  msWire({
    id: 'fb-repo',
    options: o.repos.map(function (r) { return { value: r.id, label: r.label }; }),
    selected: sel.slice(),
    emptyText: 'No repos yet.',
    onChange: function (vals) {
      state.scope.repos = vals;
      fireScopeChange();
    },
  });
}

// Re-fetch the per-team option lists (repos, users) for whatever team
// state.scope.team now holds, and repaint the active mount from them. Exported for
// scope changes made OUTSIDE this bar — the Highlights scope control (home.ts).
// Resolves either way: a failed fetch keeps the prior options, so it never blocks.
export function refreshFilterOptions() {
  var qs = state.scope.team ? '?team=' + encodeURIComponent(state.scope.team) : '';
  return get('/api/filters' + qs)
    .then(function (opts) {
      if (opts) { state.filterOpts = opts; renderScopeControls(); }
    })
    .catch(function () { /* keep the prior options */ });
}

// Team change: switching teams resets the repo AND user sub-filters (the old
// team's repos/users don't apply), re-runs the mount's onChange IMMEDIATELY (not
// gated on the options fetch), then best-effort refreshes the dropdowns for the
// new team ('me' survives — the viewer is the viewer in any team).
function onTeamChange(teamId: string): void {
  state.scope.team = teamId;
  state.scope.repos = []; // a team switch clears the repo narrowing
  if (state.scope.user && state.scope.user !== 'me') state.scope.user = '';
  fireScopeChange(); // rescope now, with the new team + all its repos/users
  refreshFilterOptions();
}

// Run the active mount's onChange, and always keep the Dashboard + Highlights in
// sync (they share state.scope): a scope change on the Skills tab must not leave
// the Dashboard contradicting it when the user switches back.
function fireScopeChange(): void {
  if (mount) mount.onChange();
  if (state.view !== 'dashboard') onScopeChange();
}

// The Dashboard's scope-change reload: headline row, open expansion, and (only
// when on screen) the distribution cards. Also flags Highlights stale. Exported
// because Highlights shares state.scope (home.ts) and mounts this bar too.
export function onScopeChange() {
  state.hlStale = true;
  loadKpis();
  renderOpenMetric();
  // scopeQs() emits '&'-prefixed params, so flip the first into a '?'.
  get('/api/outcome-types' + scopeQs().replace('&', '?')).then(function (t) {
    state.outcomeTypes = t || [];
    if ($('#sr-controls')) renderSrControls();
  });
  if ($('#sm-dists') && state.overview) {
    loadFacets().then(function () { renderDists(state.overview); });
  }
}
